import {
  UserSearch, CalendarHeart, HeartPulse, Video, FlaskConical, Siren, Building2, GraduationCap, ArrowRight,
  type LucideIcon,
} from "lucide-react";
import { Reveal } from "./Reveal";
import type { QuickAccessSettings as QuickAccessSettingsData } from "@/hooks/useHomeSettings";

const ICON_MAP: Record<string, LucideIcon> = {
  UserSearch,
  CalendarHeart,
  HeartPulse,
  Video,
  FlaskConical,
  Siren,
  Building2,
  GraduationCap,
  ArrowRight,
};

interface QuickAccessItem {
  icon: LucideIcon;
  title: string;
  description: string;
  link: string;
}

const defaultItems: QuickAccessItem[] = [
  { icon: UserSearch, title: "Find a Doctor", description: "Search by specialty, availability & language", link: "/doctors" },
  { icon: CalendarHeart, title: "Book Appointment", description: "Schedule in under 60 seconds", link: "/doctors" },
  { icon: HeartPulse, title: "Health Packages", description: "Preventive checkups for every stage", link: "/health-packages" },
  { icon: Video, title: "Telemedicine", description: "Video consult from anywhere in Nepal", link: "/contact" },
  { icon: FlaskConical, title: "Lab Reports", description: "Download diagnostic results online", link: "/contact" },
  { icon: Siren, title: "Emergency Care", description: "24/7 ambulance & trauma response", link: "/contact" },
  { icon: Building2, title: "Departments", description: "Explore 12+ medical specialities", link: "/departments" },
  { icon: GraduationCap, title: "Medical Education", description: "MBBS, residency & research programs", link: "/about" },
];

function mapItems(data: QuickAccessSettingsData["items"]): QuickAccessItem[] {
  return data.map((item) => ({
    icon: ICON_MAP[item.icon] || ArrowRight,
    title: item.title,
    description: item.description,
    link: item.link,
  }));
}

export function QuickAccess({ settings }: { settings?: QuickAccessSettingsData | null }) {
  const heading = settings?.heading || "Patient Services";
  const subtitle = settings?.subtitle || "Everything you need, one click away.";
  const description = settings?.description || "";
  const items = settings?.items && settings.items.length > 0 ? mapItems(settings.items) : defaultItems;

  return (
    <section className="relative overflow-hidden py-28">
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-0 h-[420px] w-[680px] -translate-x-1/2 rounded-full opacity-40 blur-3xl"
        style={{ background: "radial-gradient(closest-side, oklch(0.78 0.12 85 / 0.18), transparent 70%)" }}
      />

      <div className="relative mx-auto max-w-7xl px-6">
        <div className="mb-16 flex flex-col items-start justify-between gap-8 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <div className="mb-5 flex items-center gap-3">
              <span className="h-px w-10 bg-gold" />
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gold">
                {heading}
              </p>
            </div>
            <h2 className="font-display text-4xl font-semibold leading-[1.05] text-primary md:text-5xl">
              {subtitle}
            </h2>
            {description && (
              <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground">
                {description}
              </p>
            )}
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {items.map(({ icon: Icon, title, description: desc, link }, idx) => (
            <Reveal key={title} delay={idx * 60} className="h-full">
            <a
              href={link}
              className="group relative flex flex-col overflow-hidden rounded-2xl border border-border/70 bg-gradient-to-br from-card to-primary-soft/30 p-7 shadow-soft transition-all duration-500 hover:-translate-y-1.5 hover:border-primary/30 hover:shadow-elevated"
            >
              <span className="absolute right-6 top-6 font-display text-xs font-medium tracking-widest text-gold/70">
                {String(idx + 1).padStart(2, "0")}
              </span>

              <div className="relative mb-6 inline-flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary/85 text-primary-foreground shadow-glow transition-all duration-500 group-hover:rotate-[-4deg] group-hover:scale-105">
                <Icon className="h-6 w-6" strokeWidth={1.75} />
                <span
                  aria-hidden
                  className="absolute inset-0 rounded-xl ring-1 ring-gold/0 transition-all duration-500 group-hover:ring-2 group-hover:ring-gold/60 group-hover:ring-offset-2 group-hover:ring-offset-card"
                />
              </div>

              <h3 className="font-display font-space text-lg font-semibold leading-snug text-foreground transition-colors group-hover:text-primary">
                {title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {desc}
              </p>

              <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary opacity-0 transition-all duration-500 group-hover:opacity-100">
                Learn more
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
              </span>

              <span
                aria-hidden
                className="absolute bottom-0 left-0 h-[2px] w-0 bg-gradient-to-r from-gold via-gold/70 to-transparent transition-all duration-700 group-hover:w-full"
              />
            </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
